import React, { useEffect, useState } from 'react';
import { useSession } from '@/hooks/useSession';
import { Loader } from '@/components/custom/loader';
import { DashboardSummaryCard } from './dashboard-summary-card';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { usePlatformAbilities } from '@/hooks/usePlatformAbilities';
import { DashboardSection, DashboardSectionTitle, DashboardSummaryWrapper } from '@/components/custom/dashboard';
import Breadcrumbs from '@/components/custom/breadcrumbs/breadcrumbs';
import { DashboardTeacherHero } from './home-teacher/dashboard-teacher-hero';
import { TeacherShortcuts } from './home-teacher/teacher-shortcuts';
import { WarningWall, WarningWallSlider } from '@/components/custom/warning-wall';
import { isPlayfulTheme } from '@/lib/layout-version';

export const DashboardTeacher = () => {
  const { session } = useSession();
  const client = useClient();
  const [pendingCorrections, setPendingCorrections] = useState();
  const [banners, setBanners] = useState();
  const [bannersLoading, setBannersLoading] = useState(true);
  const { abilities } = usePlatformAbilities();
  const isPlayful = isPlayfulTheme();

  const getPendingCorrections = async () => {
    const { data, error } = await client.fetch('/dashboards/cards');

    if (error) {
      if (error?.status !== '401') {
        toast.error('Erro ao buscar correções pendentes');
      }
    } else {
      setPendingCorrections(data);
    }
  };

  const getBanners = async () => {
    const { data, error } = await client.fetch('/bulletin_boards/active');

    if (error) {
      if (error?.status !== '401') {
        toast.error('Erro ao buscar banners');
      }
    } else {
      setBanners(data);
    }
    setBannersLoading(false);
  };

  useEffect(() => {
    // No playful, "Para corrigir" saiu (virou parte do painel de itens de
    // atenção do hero, que busca esse mesmo dado por conta própria) — sem
    // consumidor aqui, essa busca não precisa mais rodar nesse tema.
    if (!isPlayful) {
      getPendingCorrections();
    }
  }, [isPlayful]);

  useEffect(() => {
    getBanners();
  }, []);

  const bannerContent = bannersLoading ? (
    <Loader />
  ) : banners?.length > 1 ? (
    <WarningWallSlider
      data={banners}
      className={isPlayful ? 'mb-0' : undefined}
    />
  ) : (
    <WarningWall
      warning={banners?.[0]}
      key={banners?.[0]?.id}
      className={isPlayful ? 'mb-0' : undefined}
    />
  );

  const breadcrumbs = {
    title: 'Início',
    nav: [
      {
        route: '/',
        name: `Olá, ${session?.user?.name}`,
        isActive: true
      }
    ]
  };

  return (
    <>
      <Breadcrumbs data={breadcrumbs} />

      {!isPlayful && bannerContent}

      {/* O hero (Tia Banner, Próxima aula/Pendências, Minhas turmas) é uma
          peça do tema playful — não deve renderizar no default. */}
      {isPlayful && <DashboardTeacherHero banner={bannerContent} />}

      {/* "Para corrigir" só existe no tema Default — no playful ela duplicava
          com a seção Atalhos (mesmos destinos de Atividades), então foi
          removida por decisão da usuária ao ver a Home ao vivo. */}
      {!isPlayful && (
        <DashboardSection>
          <DashboardSectionTitle>Para corrigir</DashboardSectionTitle>

          <DashboardSummaryWrapper className="mb-0">
            {abilities?.redacao && (
              <DashboardSummaryCard
                title="Redações para revisar"
                count={pendingCorrections?.['surveys_count']}
                link="Acessar redações"
                route="/redacao"
              />
            )}
            {abilities?.projetos && (
              <DashboardSummaryCard
                title="Projetos (versões para avaliar)"
                count={pendingCorrections?.['project_versions_count']}
                link="Acessar projetos"
                route="/projetos/grupos"
              />
            )}
            {abilities?.['cursos-ativ-complementar'] && (
              <DashboardSummaryCard
                title="Atividades para avaliar"
                count={pendingCorrections?.['activities_count']}
                link="Acessar atividades"
                route="/atividade-complementar"
              />
            )}
          </DashboardSummaryWrapper>
        </DashboardSection>
      )}

      {isPlayful && <TeacherShortcuts />}
    </>
  );
};
